import * as React from 'react';
import { Warning } from 'rcd-cpp-ui/ui/alert';
import { useSelector } from '../../../utils/useSelector';
import { Role } from '../../../enums/Role';
import { RoleId } from '../../../enums/RoleId';
import { WorkflowStepType } from '../../../enums/WorkflowStepType';
import {
  getAllRoles,
  isOwner,
  isPlanner
} from '../../AccessControl/getPermission';
import { useConfigureWorkflowPermissions } from './useConfigureWorkflowPermissions';
import { useAlert } from '../../Alert/alert';
import { useLoader } from '../../Loader/loader';
import {
  ConfigureWorkflowWarningType,
  getConfigureWorkflowTypeEnums,
  getCreativeHelpDisabled,
  getDataValues,
  getInheritedContentTypeConfiguration,
  getSchemaFieldNameByField,
  getSkipWorkflowStatuses,
  getWarningMessages,
  isModified
} from '../../../utils';
import { WorkflowStep } from '../../../types/WorkflowStep';
import { WorkflowInfo } from '../../../types/WorkflowInfo';
import { ItemStatus, CreativeStatus } from '../../../enums/ItemStatus';
import { ConfigureWorkflowType } from '../../../enums/ConfigureWorkflowType';
import { ConfigureWorkflowTree } from './configureWorkflowTree';

import { Metadata } from '../../../types';
import {
  WorkflowStageUiConfigById,
  WorkflowSavePayload
} from '../../../types/ConfigureWorkflowTypes';
import { useActions } from '../metadataFormActions';

const defaultSkips = [ItemStatus.LightUpdateEditorReview];

type ConfigureLightUpdateWorkflowProps = {
  onClose: () => void;
  data: Partial<Metadata>;
  onSave: (payload: WorkflowSavePayload) => Promise<void>;
  workflowState: WorkflowSavePayload;
  onWorkflowStateChange: (update: Partial<WorkflowSavePayload>) => void;
  onSaveDisabledChange?: (disabled: boolean) => void;
  errorMessage?: string;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onIsModalDisabledChange?: (disabled: boolean) => void;
  isSaving?: boolean;
  isNewLightUpdate?: boolean;
};

export const ConfigureLightUpdateWorkflow = ({
  onClose,
  data,
  onSave,

  workflowState,
  onWorkflowStateChange,
  onSaveDisabledChange = () => null,
  errorMessage,
  onHasChangesChange = () => null,
  onIsModalDisabledChange = () => null,
  isSaving = false,
  isNewLightUpdate = false
}: ConfigureLightUpdateWorkflowProps) => {
  const { reloadData } = useActions();
  const {
    configureWorkflowSkipIds,
    configureWorkflowType,
    requestCreativeHelp
  } = workflowState;
  const empCd = useSelector(s => s.app.user.employeeId);
  const isAdmin = useSelector(s => s.app.user.roles?.includes(Role.admin));
  const isBackupTM = useSelector(s =>
    s.view?.backingUpFor?.roleIds?.includes(RoleId.teamManager)
  );
  const getAPI = useSelector(s => s.app.elFn.getAPI);
  const typeEnums = getConfigureWorkflowTypeEnums(
    'configureLightUpdateWorkflowType'
  );
  const schemas = useSelector(s => s.planning.selectedWorkspace.schemas);
  const requestCreativeHelpFieldName = getSchemaFieldNameByField(
    schemas,
    'requestCreativeHelp'
  );
  const workspaceRefId = useSelector(
    s => s?.planning?.selectedWorkspace?.props?.refId
  );

  const {
    itemRefId,
    homeWorkspaceRefId,
    itemStatusId,
    itemCreativeStatusId,
    researchModelId,
    contentTypeId,
    subtypeId,
    uniOf,
    evolvingInsights,
    leadAuthorEmpCd,
    isExcludeFromEditorialTokenCount,
    isExcludeFromCreativeTokenCount,
    isProofreadingOnly
  } = getDataValues(data);

  const isWorkspaceOwner = useSelector(s =>
    isOwner(getAllRoles(s.accessControl, workspaceRefId))
  );

  const isWorkspacePlanner = useSelector(s =>
    isPlanner(getAllRoles(s.accessControl, workspaceRefId))
  );

  const config = useSelector(s =>
    getInheritedContentTypeConfiguration(
      s.planning.contentTypeConfigurations,
      researchModelId,
      contentTypeId,
      subtypeId
    )
  );

  const [collapsed, setCollapsed] = React.useState({ creative: false });
  const [workflowInfo, setWorkflowInfo] = React.useState<WorkflowInfo>(null);
  const [workflowSteps, setWorkflowSteps] =
    React.useState<WorkflowStep[]>(null);
  const [creativeHelpDisabled, setCreativeHelpDisabled] =
    React.useState<boolean>(false);

  const initialConfigureWorkflowSkipIds = React.useMemo(
    () => data.props?.configureLightUpdateWorkflowSkipIds,
    [data]
  );
  const [editorialTokenCount, setEditorialTokenCount] = React.useState<number>(
    workflowInfo?.allocatedEditorialWorkflowCount ?? null
  );
  const [creativeTokenCount, setCreativeTokenCount] =
    React.useState<number>(null);
  const [plannedEditorialTokenCount, setPlannedEditorialTokenCount] =
    React.useState<number>(null);
  const [plannedCreativeTokenCount, setPlannedCreativeTokenCount] =
    React.useState<number>(null);

  const { isLoading, Loader, Alert, setLoading, setMessage } = useLoader(
    useAlert({})
  );

  React.useEffect(() => {
    const refId = data?.props?.refId;
    const labels = data?.labels;
    if (!refId || !labels) return;
    reloadData(refId, labels, true);
  }, []);

  const statusUiConfigById = React.useMemo<WorkflowStageUiConfigById>(
    () => ({
      [ItemStatus.LightUpdateEditorReview]: {
        showEditorialTokenCounter: true
      }
    }),
    []
  );

  const getLightUpdateWorkflowType = React.useCallback(
    (skipIds: number[] | null) => {
      if (skipIds != null) {
        if (skipIds.includes(ItemStatus.LightUpdateEditorReview)) {
          const isDefault =
            skipIds.length === defaultSkips.length &&
            defaultSkips.every(id => skipIds.includes(id));
          if (isDefault) {
            return ConfigureWorkflowType.Default;
          }
          return ConfigureWorkflowType.CustomizedProofreading;
        }
        return ConfigureWorkflowType.CustomizedEditing;
      }
      return null;
    },
    []
  );

  const getUpdatedSkipIds = React.useCallback(
    (currentIds: number[] | null, statusId: number) => {
      const newIds = [...(currentIds || [])];
      const idx = newIds.indexOf(statusId);
      if (idx > -1) {
        newIds.splice(idx, 1);
        if (
          statusId == ItemStatus.LightUpdateEditorReview &&
          !newIds.includes(ItemStatus.LightUpdateProofreading)
        ) {
          newIds.push(ItemStatus.LightUpdateProofreading);
        } else if (
          statusId == ItemStatus.LightUpdateProofreading &&
          !newIds.includes(ItemStatus.LightUpdateEditorReview)
        ) {
          newIds.push(ItemStatus.LightUpdateEditorReview);
        }
      } else {
        newIds.push(statusId);
        if (
          statusId == ItemStatus.LightUpdateEditorReview &&
          newIds.includes(ItemStatus.LightUpdateProofreading)
        ) {
          newIds.splice(newIds.indexOf(ItemStatus.LightUpdateProofreading), 1);
        } else if (
          statusId == ItemStatus.LightUpdateProofreading &&
          newIds.includes(ItemStatus.LightUpdateEditorReview)
        ) {
          newIds.splice(newIds.indexOf(ItemStatus.LightUpdateEditorReview), 1);
        }
      }
      newIds.sort((a, b) => a - b);
      return newIds;
    },
    []
  );

  const handleWorkflowStageChange = React.useCallback(
    (statusId: number) => {
      const newIds = getUpdatedSkipIds(configureWorkflowSkipIds, statusId);
      const newWorkflowType = getLightUpdateWorkflowType(newIds);
      onWorkflowStateChange({ configureWorkflowSkipIds: newIds });
      onWorkflowStateChange({ configureWorkflowType: newWorkflowType });
    },
    [configureWorkflowSkipIds, getLightUpdateWorkflowType, getUpdatedSkipIds]
  );

  const handleRequestCreativeHelpChange = React.useCallback(
    (checked: boolean) => {
      onWorkflowStateChange({ requestCreativeHelp: checked });
    },
    []
  );

  const { canConfigureCreativeHelp, canConfigureWorkflow, canSaveWorkflow } =
    useConfigureWorkflowPermissions({
      empCd,
      leadAuthorEmpCd,
      itemCreativeStatusId,
      workflowInfo,
      isBackupTM,
      isAdmin,
      isWorkspaceOwner,
      isWorkspacePlanner,
      contentTypeId,
      itemStatusId
    });
  const hasChanges = isModified(
    data,
    configureWorkflowSkipIds,
    requestCreativeHelp,
    initialConfigureWorkflowSkipIds
  );

  const warnings = getWarningMessages(
    false,
    false,
    canConfigureWorkflow,
    leadAuthorEmpCd,
    workflowInfo,
    researchModelId,
    contentTypeId,
    config,
    requestCreativeHelpFieldName,
    canConfigureCreativeHelp,
    canSaveWorkflow
  );

  const isWorkflowDisabled =
    warnings.filter(w =>
      [
        ConfigureWorkflowWarningType.WORKFLOW,
        ConfigureWorkflowWarningType.BOTH
      ].includes(w.warningType)
    ).length > 0 ||
    (itemStatusId >= ItemStatus.LightUpdateEditingIntake &&
      itemStatusId <= ItemStatus.LightUpdatePrepPublication) ||
    (canSaveWorkflow && !canConfigureWorkflow);

  const isModalDisabled =
    warnings.filter(w =>
      [ConfigureWorkflowWarningType.BOTH].includes(w.warningType)
    ).length > 0;

  React.useEffect(() => {
    setLoading(isSaving);
  }, [isSaving, setLoading]);

  React.useEffect(() => {
    if (errorMessage) setMessage(errorMessage);
  }, [errorMessage, setMessage]);

  React.useEffect(() => {
    const canMakeChanges = !isWorkflowDisabled || !creativeHelpDisabled;
    onSaveDisabledChange(
      isLoading ||
        isModalDisabled ||
        (canMakeChanges && !hasChanges && !isNewLightUpdate)
    );
    onHasChangesChange(hasChanges);
    onIsModalDisabledChange(isModalDisabled);
  }, [
    isLoading,
    isModalDisabled,
    isWorkflowDisabled,
    creativeHelpDisabled,
    hasChanges,
    isNewLightUpdate,
    onSaveDisabledChange,
    onHasChangesChange,
    onIsModalDisabledChange
  ]);

  React.useEffect(() => {
    const { getWorkflowSteps, getWorkflowInfo } = getAPI();

    const isLightUpdateWorkflowStatus = (statusId: number) =>
      statusId >= ItemStatus.LightUpdateDraft &&
      statusId <= ItemStatus.LightUpdatePrepPublication;

    setLoading(true);
    Promise.all([
      getWorkflowInfo(itemRefId),
      getWorkflowSteps(WorkflowStepType.LightUpdate)
    ])
      .then(([wInfo, steps]) => {
        if (!config || !steps)
          throw new Error('Invalid configuration or steps');

        setWorkflowInfo(wInfo);

        const skips = getSkipWorkflowStatuses(
          config,
          !!uniOf,
          evolvingInsights,
          isProofreadingOnly
        );
        const newWorkflowSteps = steps.reduce((acc, ws) => {
          ws.statuses = ws.statuses?.filter(s => {
            if (skips.includes(s.statusId)) return false;
            return isLightUpdateWorkflowStatus(s.statusId);
          });
          if (ws.statuses?.length > 0) {
            const hasLightUpdateEditorReview = ws.statuses.some(
              s => s.statusId == ItemStatus.LightUpdateEditorReview
            );
            const hasLightUpdateProofreading = ws.statuses.some(
              s => s.statusId == ItemStatus.LightUpdateProofreading
            );
            ws.statuses.forEach(s => {
              if (s.statusId <= itemStatusId) s.disabled = true;
              if (s.statusId === ItemStatus.LightUpdateDraft) {
                s.disabled = true;
              }
              if (
                (s.statusId == ItemStatus.LightUpdateEditorReview &&
                  !hasLightUpdateProofreading) ||
                (s.statusId == ItemStatus.LightUpdateProofreading &&
                  !hasLightUpdateEditorReview)
              ) {
                s.disabled = true;
              }
              switch (s.statusId) {
                case ItemStatus.LightUpdateProofreading:
                  if (isProofreadingOnly) s.disabled = true;
                  break;
              }
            });
            if (ws.statuses.length > 0) {
              acc.push(ws);
            }
          }
          return acc;
        }, []);
        setWorkflowSteps(newWorkflowSteps);

        setCreativeHelpDisabled(
          getCreativeHelpDisabled(
            wInfo,
            itemCreativeStatusId,
            data,
            config,
            itemStatusId,
            isNewLightUpdate
          )
        );
      })
      .catch(err => {
        console.error('Failed to load workflow steps/configurations', err);
        setMessage('Failed to load workflow steps/configurations');
      })
      .finally(() => {
        if (!isSaving) setLoading(false);
      });
  }, [
    researchModelId,
    contentTypeId,
    subtypeId,
    uniOf,
    isProofreadingOnly,
    leadAuthorEmpCd,
    itemStatusId,
    itemRefId
  ]);

  React.useEffect(() => {
    onWorkflowStateChange({
      requestCreativeHelp: isNewLightUpdate
        ? false
        : (data?.props?.requestCreativeHelp ?? false)
    });
  }, []);

  React.useEffect(() => {
    onWorkflowStateChange({
      configureWorkflowType: getLightUpdateWorkflowType(
        configureWorkflowSkipIds
      )
    });
  }, [configureWorkflowSkipIds, getLightUpdateWorkflowType]);

  React.useEffect(() => {
    if (configureWorkflowSkipIds == null && workflowSteps != null) {
      onWorkflowStateChange({ configureWorkflowSkipIds: defaultSkips });
    } else if (
      configureWorkflowSkipIds?.includes(ItemStatus.LightUpdateDraft) &&
      workflowSteps != null
    ) {
      onWorkflowStateChange({
        configureWorkflowSkipIds: configureWorkflowSkipIds.filter(
          id => id !== ItemStatus.LightUpdateDraft
        )
      });
    }
  }, [configureWorkflowSkipIds, workflowSteps]);

  React.useEffect(() => {
    const shouldAddCreativeToken =
      (!!workflowInfo?.hasNotCompletedCreative || isNewLightUpdate) &&
      requestCreativeHelp &&
      !isExcludeFromCreativeTokenCount;
    if (typeof workflowInfo?.allocatedCreativeWorkflowCount == 'number') {
      let newCount = workflowInfo.allocatedCreativeWorkflowCount;
      if (
        shouldAddCreativeToken &&
        itemCreativeStatusId?.props?.id > CreativeStatus.WaitingForDesign &&
        itemCreativeStatusId?.props?.id <=
          CreativeStatus.CreativeAdditionalCreativeDesignReview
      )
        newCount += 1;
      setCreativeTokenCount(newCount);
    }

    if (typeof workflowInfo?.plannedCreativeWorkflowCount == 'number') {
      let newCount = workflowInfo.plannedCreativeWorkflowCount;
      if (
        shouldAddCreativeToken &&
        (!itemCreativeStatusId ||
          itemCreativeStatusId?.props?.id <= CreativeStatus.WaitingForDesign ||
          isNewLightUpdate)
      ) {
        newCount += 1;
      }
      setPlannedCreativeTokenCount(newCount);
    }
  }, [
    requestCreativeHelp,
    workflowInfo,
    isExcludeFromCreativeTokenCount,
    isNewLightUpdate
  ]);

  React.useEffect(() => {
    const shouldAddEditorialToken =
      !isExcludeFromEditorialTokenCount &&
      (workflowInfo?.isWipItem || isNewLightUpdate);
    if (typeof workflowInfo?.allocatedEditorialWorkflowCount == 'number') {
      let newCount = workflowInfo.allocatedEditorialWorkflowCount;
      if (
        shouldAddEditorialToken &&
        !configureWorkflowSkipIds?.includes(
          ItemStatus.LightUpdateEditorReview
        ) &&
        itemStatusId > ItemStatus.WaitingforLUEditing &&
        itemStatusId < ItemStatus.LightUpdatePrepPublication
      ) {
        newCount += 1;
      }
      setEditorialTokenCount(newCount);
    }
    if (typeof workflowInfo?.plannedEditorialWorkflowCount == 'number') {
      let newCount = workflowInfo.plannedEditorialWorkflowCount;
      if (
        shouldAddEditorialToken &&
        !configureWorkflowSkipIds?.includes(
          ItemStatus.LightUpdateEditorReview
        ) &&
        itemStatusId <= ItemStatus.WaitingforLUEditing
      ) {
        newCount += 1;
      }
      setPlannedEditorialTokenCount(newCount);
    }
  }, [
    configureWorkflowSkipIds,
    workflowInfo,
    isExcludeFromEditorialTokenCount,
    isNewLightUpdate
  ]);
  return (
    <div className="slider-activator">
      <div className="workflow-modal-body">
        {warnings.map(w => (
          <Warning key={w.message}>{w.message}</Warning>
        ))}
        <Loader />
        <Alert />
        {configureWorkflowType && (
          <div>
            <label>Selected Workflow Type:&nbsp;</label>
            <b>{typeEnums[configureWorkflowType] || configureWorkflowType}</b>
          </div>
        )}
        <ConfigureWorkflowTree
          workflowSteps={workflowSteps}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          configureWorkflowSkipIds={configureWorkflowSkipIds}
          onWorkflowStageChange={handleWorkflowStageChange}
          isLoading={isLoading}
          isWorkflowDisabled={isWorkflowDisabled}
          workflowInfo={workflowInfo}
          isNewLightUpdate={isNewLightUpdate}
          editorialTokenCount={editorialTokenCount}
          plannedEditorialTokenCount={plannedEditorialTokenCount}
          creativeHelpDisabled={creativeHelpDisabled}
          isModalDisabled={isModalDisabled}
          requestCreativeHelp={requestCreativeHelp}
          onRequestCreativeHelpChange={handleRequestCreativeHelpChange}
          creativeTokenCount={creativeTokenCount}
          plannedCreativeTokenCount={plannedCreativeTokenCount}
          statusUiConfigById={statusUiConfigById}
          requestCreativeHelpFieldName={requestCreativeHelpFieldName}
          canConfigureCreativeHelp={canConfigureCreativeHelp}
          showPreProductionExternalReview={false}
          showPostProductionExternalReview={false}
        />
      </div>
    </div>
  );
};
